import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import { waybillAPI, uomAPI } from '../../api/waybillAPI';

const PHP = (n) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const BILLING_TYPES = [
  { value: 'per_pc',  label: 'Per PC' },
  { value: 'per_kg',  label: 'Per KG' },
  { value: 'per_cbm', label: 'Per CBM' },
];

const billingLabel = (v) => BILLING_TYPES.find((t) => t.value === v)?.label || v || '';

function computeSubTotal(f) {
  const w   = parseFloat(f.Width  || 0) / 100;
  const l   = parseFloat(f.Length || 0) / 100;
  const h   = parseFloat(f.Height || 0) / 100;
  const wt  = parseFloat(f.Weight      || 0);
  const fr  = parseFloat(f.Freight     || 0);
  const qty = parseFloat(f.Quantity    || 0);
  const adj = parseFloat(f.Adjustments || 0);
  let base = 0;
  if (f.BillingType === 'per_kg')       base = wt * fr * qty;
  else if (f.BillingType === 'per_cbm') base = w * l * h * fr * qty;
  else                                   base = qty * fr;
  return base + adj;
}

const emptyEditForm = {
  Description: '', UnitOfMeasureId: '', BillingType: 'per_pc',
  Width: '', Length: '', Height: '', Weight: '', Freight: '',
  Quantity: '', Adjustments: '0',
  VanNo: '', SealNo: '',
};

const inputCls      = 'w-full border-b border-teal-400 focus:outline-none text-sm py-0.5 px-1 bg-teal-50';
const inputRightCls = 'w-full border-b border-teal-400 focus:outline-none text-sm py-0.5 px-1 bg-teal-50 text-right';

export default function WaybillLineTable({ waybillId, hasWithholding = false, uomOptions = [], minAmount = 250, showVanSeal = false, initialVanNo = '', initialSealNo = '', onGLUpdate }) {
  const [lines,      setLines]      = useState([]);
  const [editingId,  setEditingId]  = useState(null); // _id | 'new' | null
  const [editForm,   setEditForm]   = useState({});
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [defaultVanNo,  setDefaultVanNo]  = useState('');
  const [defaultSealNo, setDefaultSealNo] = useState('');
  const [applying,   setApplying]   = useState(false);
  const [lineStatus, setLineStatus] = useState(null); // { type: 'success'|'error', msg: string }

  const descInputRef = useRef(null);

  const loadLines = useCallback(() => {
    if (!waybillId) return;
    setLoading(true);
    waybillAPI.getLines(waybillId)
      .then((r) => setLines(r.data?.data || r.data || []))
      .catch(() => setLines([]))
      .finally(() => setLoading(false));
  }, [waybillId]);

  useEffect(() => { loadLines(); }, [loadLines]);

  useEffect(() => {
    if (editingId === 'new' && descInputRef.current) {
      descInputRef.current.focus();
    }
  }, [editingId]);

  // CHANGE 6 — legacy FF waybill: pre-seed default Van/Seal from header if all lines are empty.
  // Runs once after lines finish loading. User still clicks "Apply to all lines" to persist.
  useEffect(() => {
    if (!showVanSeal || loading) return;
    if (!initialVanNo && !initialSealNo) return;
    const allEmpty = lines.every((ln) => !(ln.VanNo || '') && !(ln.SealNo || ''));
    if (!allEmpty) return;
    if (initialVanNo)  setDefaultVanNo((prev) => prev || initialVanNo);
    if (initialSealNo) setDefaultSealNo((prev) => prev || initialSealNo);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]); // intentionally fires only when loading transitions (true → false)

  // Auto-clear status banner after 3 s
  useEffect(() => {
    if (!lineStatus) return;
    const t = setTimeout(() => setLineStatus(null), 3000);
    return () => clearTimeout(t);
  }, [lineStatus]);

  function setField(key) {
    return (e) => setEditForm((prev) => ({ ...prev, [key]: e.target.value }));
  }

  function startEdit(line) {
    setEditingId(line._id);
    setEditForm({
      Description:     line.Description || '',
      UnitOfMeasureId: line.UnitOfMeasureId?._id || line.UnitOfMeasureId || '',
      BillingType:     line.BillingType || 'per_pc',
      Width:           String((line.Width  || 0) * 100),
      Length:          String((line.Length || 0) * 100),
      Height:          String((line.Height || 0) * 100),
      Weight:          String(line.Weight      || ''),
      Quantity:        String(line.Quantity    || ''),
      Freight:         String(line.Freight     || ''),
      Adjustments:     String(line.Adjustments || 0),
      VanNo:           line.VanNo  || '',
      SealNo:          line.SealNo || '',
    });
  }

  function handleCancelEdit() {
    setEditingId(null);
    setEditForm({});
  }

  function handleAddLine() {
    setEditingId('new');
    setEditForm({ ...emptyEditForm });
  }

  async function handleSaveLine() {
    setSaving(true);
    const payload = {
      Description:     editForm.Description,
      UnitOfMeasureId: editForm.UnitOfMeasureId || undefined,
      BillingType:     editForm.BillingType,
      Width:           parseFloat(editForm.Width  || 0) / 100,
      Length:          parseFloat(editForm.Length || 0) / 100,
      Height:          parseFloat(editForm.Height || 0) / 100,
      Weight:          parseFloat(editForm.Weight      || 0),
      Quantity:        parseFloat(editForm.Quantity    || 0),
      Freight:         parseFloat(editForm.Freight     || 0),
      Adjustments:     parseFloat(editForm.Adjustments || 0),
      SubTotal:        computeSubTotal(editForm),
      VanNo:           (editForm.VanNo  || '').toUpperCase(),
      SealNo:          (editForm.SealNo || '').toUpperCase(),
    };
    if (!payload.UnitOfMeasureId) delete payload.UnitOfMeasureId;
    try {
      if (editingId === 'new') {
        const res = await waybillAPI.addLine(waybillId, payload);
        const newLine = res.data?.data || res.data;
        setLines((prev) => [...prev, newLine]);
        if (res.data?.waybillGL && onGLUpdate) onGLUpdate(res.data.waybillGL);
      } else {
        const res = await waybillAPI.updateLine(waybillId, editingId, payload);
        const updated = res.data?.data || res.data;
        setLines((prev) => prev.map((l) => (l._id === editingId ? updated : l)));
        if (res.data?.waybillGL && onGLUpdate) onGLUpdate(res.data.waybillGL);
      }
      setEditingId(null);
      setEditForm({});
      setLineStatus({ type: 'success', msg: 'Line saved.' });
    } catch (e) {
      console.error('[WaybillLineTable] save error', e);
      const msg = e?.response?.data?.detail || e?.response?.data?.error || 'Failed to save line.';
      setLineStatus({ type: 'error', msg });
    } finally {
      setSaving(false);
    }
  }

  async function applyDefaultsToAllLines() {
    if (!defaultVanNo && !defaultSealNo) return;
    const toUpdate = lines.filter(
      (ln) => (defaultVanNo && !(ln.VanNo || '')) || (defaultSealNo && !(ln.SealNo || ''))
    );
    if (!toUpdate.length) return;
    // Optimistic local update — never overwrites an already-set value
    setLines((prev) => prev.map((ln) => ({
      ...ln,
      VanNo:  ln.VanNo  || defaultVanNo,
      SealNo: ln.SealNo || defaultSealNo,
    })));
    setApplying(true);
    try {
      await Promise.all(toUpdate.map((ln) =>
        waybillAPI.updateLine(waybillId, ln._id, {
          Description:     ln.Description || '',
          UnitOfMeasureId: ln.UnitOfMeasureId?._id || ln.UnitOfMeasureId || undefined,
          BillingType:     ln.BillingType || 'per_pc',
          Width:           ln.Width    || 0,
          Length:          ln.Length   || 0,
          Height:          ln.Height   || 0,
          Weight:          ln.Weight   || 0,
          Quantity:        ln.Quantity || 0,
          Freight:         ln.Freight  || 0,
          Adjustments:     ln.Adjustments || 0,
          VanNo:           (ln.VanNo  || defaultVanNo).toUpperCase(),
          SealNo:          (ln.SealNo || defaultSealNo).toUpperCase(),
        })
      ));
      setLineStatus({ type: 'success', msg: 'Van/Seal applied to all empty lines.' });
    } catch (e) {
      console.error('[WaybillLineTable] applyDefaults error', e);
      setLineStatus({ type: 'error', msg: 'Failed to apply defaults to some lines.' });
      loadLines(); // reload to sync with actual DB state
    } finally {
      setApplying(false);
    }
  }

  async function handleDelete(lineId) {
    if (!window.confirm('Delete this line item?')) return;
    try {
      const res = await waybillAPI.deleteLine(waybillId, lineId);
      setLines((prev) => prev.filter((l) => l._id !== lineId));
      if (res.data?.waybillGL && onGLUpdate) onGLUpdate(res.data.waybillGL);
      setLineStatus({ type: 'success', msg: 'Line deleted.' });
    } catch (e) {
      setLineStatus({ type: 'error', msg: 'Failed to delete line.' });
    }
  }

  // Footer calculations
  const rawTotal    = lines.reduce((s, l) => s + (l.SubTotal  || 0), 0);
  const totalAmount = Math.max(rawTotal, minAmount);
  const totalQty    = lines.reduce((s, l) => s + (l.Quantity  || 0), 0);
  const netOfVAT    = totalAmount / 1.12;
  const vat         = netOfVAT * 0.12;
  const withholding = netOfVAT * 0.02;
  const amountDue   = hasWithholding ? totalAmount - withholding : totalAmount;

  function uomLabel(rawId) {
    const id = rawId?._id || rawId;
    const found = uomOptions.find((u) => u._id === id);
    return found ? (found.Abbreviation || found.UnitOfMeasureName || '') : '';
  }

  function renderReadOnlyRow(line) {
    return (
      <tr key={line._id} className="hover:bg-gray-50 border-b border-gray-100">
        {showVanSeal && <td className="px-2 py-1.5 text-sm text-gray-800 font-mono">{line.VanNo  || '—'}</td>}
        {showVanSeal && <td className="px-2 py-1.5 text-sm text-gray-800 font-mono">{line.SealNo || '—'}</td>}
        <td className="px-2 py-1.5 text-sm text-gray-800">{line.Description ?? ''}</td>
        <td className="px-2 py-1.5 text-sm text-gray-600">{uomLabel(line.UnitOfMeasureId)}</td>
        <td className="px-2 py-1.5 text-sm text-gray-600">{billingLabel(line.BillingType)}</td>
        <td className="px-2 py-1.5 text-sm text-gray-600 text-right">
          {line.Width  != null ? ((line.Width  || 0) * 100).toFixed(0) : '—'}
        </td>
        <td className="px-2 py-1.5 text-sm text-gray-600 text-right">
          {line.Length != null ? ((line.Length || 0) * 100).toFixed(0) : '—'}
        </td>
        <td className="px-2 py-1.5 text-sm text-gray-600 text-right">
          {line.Height != null ? ((line.Height || 0) * 100).toFixed(0) : '—'}
        </td>
        <td className="px-2 py-1.5 text-sm text-gray-600 text-right">{line.Weight ?? '—'}</td>
        <td className="px-2 py-1.5 text-sm text-gray-600 text-right">{line.Quantity ?? '—'}</td>
        <td className="px-2 py-1.5 text-sm text-gray-600 text-right">{line.Freight ?? '—'}</td>
        <td className="px-2 py-1.5 text-sm text-gray-600 text-right">{line.Adjustments ?? '0'}</td>
        <td className="px-2 py-1.5 text-sm font-medium text-gray-900 text-right">{(totalAmount > 0 ? `₱${PHP(line.SubTotal)}` : '')}</td>
        <td className="px-2 py-1.5 text-center">
          <div className="flex items-center justify-center gap-1">
            <button onClick={() => startEdit(line)} disabled={editingId !== null} className="disabled:opacity-30" title="Edit">
              <PencilSquareIcon className="h-4 w-4 text-gray-400 hover:text-teal-600" />
            </button>
            <button onClick={() => handleDelete(line._id)} disabled={editingId !== null} className="disabled:opacity-30" title="Delete">
              <TrashIcon className="h-4 w-4 text-gray-400 hover:text-red-500" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  function renderEditRow(key) {
    const liveSubTotal = computeSubTotal(editForm);
    return (
      <tr key={key} className="bg-teal-50 ring-1 ring-teal-200 ring-inset">
        {showVanSeal && (
          <td className="px-1 py-1">
            <input type="text" value={editForm.VanNo || ''}
              onChange={(e) => setField('VanNo')({ target: { value: e.target.value.toUpperCase() } })}
              placeholder="Van No." className={inputCls} />
          </td>
        )}
        {showVanSeal && (
          <td className="px-1 py-1">
            <input type="text" value={editForm.SealNo || ''}
              onChange={(e) => setField('SealNo')({ target: { value: e.target.value.toUpperCase() } })}
              placeholder="Seal No." className={inputCls} />
          </td>
        )}
        <td className="px-1 py-1">
          <input ref={editingId === 'new' ? descInputRef : null}
            type="text" value={editForm.Description} onChange={setField('Description')}
            placeholder="Description" className={inputCls} />
        </td>
        <td className="px-1 py-1">
          <select value={editForm.UnitOfMeasureId} onChange={setField('UnitOfMeasureId')} className={inputCls}>
            <option value="">—</option>
            {uomOptions.map((u) => (
              <option key={u._id} value={u._id}>{u.Abbreviation || u.UnitOfMeasureName}</option>
            ))}
          </select>
        </td>
        <td className="px-1 py-1">
          <select value={editForm.BillingType} onChange={setField('BillingType')} className={inputCls}>
            {BILLING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </td>
        <td className="px-1 py-1">
          <input type="number" step="1" min="0" value={editForm.Width}
            onChange={setField('Width')} placeholder="50" className={inputRightCls} />
        </td>
        <td className="px-1 py-1">
          <input type="number" step="1" min="0" value={editForm.Length}
            onChange={setField('Length')} placeholder="50" className={inputRightCls} />
        </td>
        <td className="px-1 py-1">
          <input type="number" step="1" min="0" value={editForm.Height}
            onChange={setField('Height')} placeholder="50" className={inputRightCls} />
        </td>
        <td className="px-1 py-1">
          <input type="number" step="0.01" min="0" value={editForm.Weight}
            onChange={setField('Weight')} className={inputRightCls} />
        </td>

        <td className="px-1 py-1">
          <input type="number" step="0.01" min="0" value={editForm.Quantity}
            onChange={setField('Quantity')} className={inputRightCls} />
        </td>
        <td className="px-1 py-1">
          <input type="number" step="0.01" min="0" value={editForm.Freight}
            onChange={setField('Freight')} className={inputRightCls} />
        </td>
        <td className="px-1 py-1">
          <input type="number" step="0.01" value={editForm.Adjustments}
            onChange={setField('Adjustments')} className={inputRightCls} />
        </td>
        <td className="px-2 py-1 text-right">
          <span className="text-sm font-medium text-teal-700">₱{PHP(liveSubTotal)}</span>
        </td>
        <td className="px-2 py-1 text-center">
          <div className="flex items-center justify-center gap-1">
            <button onClick={handleSaveLine} disabled={saving} title="Save">
              <CheckIcon className="h-4 w-4 text-teal-600 hover:text-teal-800" />
            </button>
            <button onClick={handleCancelEdit} title="Cancel">
              <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  if (loading) return <p className="text-sm text-gray-400 py-4 px-2">Loading line items…</p>;

  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-3">

      {/* Default Van / Seal helper — FF only */}
      {showVanSeal && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3 flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-blue-900 mb-1">Default Van No.</label>
            <input
              type="text"
              value={defaultVanNo}
              onChange={(e) => setDefaultVanNo(e.target.value.toUpperCase())}
              className="px-2 py-1 border rounded text-sm w-40"
              placeholder="e.g. PSAU-287064-6"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-blue-900 mb-1">Default Seal No.</label>
            <input
              type="text"
              value={defaultSealNo}
              onChange={(e) => setDefaultSealNo(e.target.value.toUpperCase())}
              className="px-2 py-1 border rounded text-sm w-40"
              placeholder="e.g. SL123456"
            />
          </div>
          <button
            type="button"
            onClick={applyDefaultsToAllLines}
            disabled={applying || (!defaultVanNo && !defaultSealNo) || editingId !== null}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {applying ? 'Applying…' : 'Apply to all lines'}
          </button>
          <p className="text-xs text-blue-800 ml-auto">
            Fills empty Van/Seal fields on every line. Existing values are kept.
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border-collapse">
          <colgroup>
            {showVanSeal && <col style={{ width: 110 }} />}
            {showVanSeal && <col style={{ width: 110 }} />}
            <col style={{ minWidth: 120 }} />
            <col style={{ width: 90  }} />
            <col style={{ width: 90  }} />
            <col style={{ width: 60  }} />
            <col style={{ width: 60  }} />
            <col style={{ width: 60  }} />
            <col style={{ width: 70  }} />
            <col style={{ width: 80  }} />
            <col style={{ width: 60  }} />
            <col style={{ width: 80  }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 72  }} />
          </colgroup>
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {showVanSeal && <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide text-left">Van No.</th>}
              {showVanSeal && <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide text-left">Seal No.</th>}
              {['Description','UOM','Billing Type','W','L','H','Weight','Qty','Freight','Adjustment','Sub Total',''].map((h, i) => (
                <th key={i}
                  className={`px-2 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide
                    ${i >= 3 && i <= 10 ? 'text-right' : i === 11 ? 'text-center' : 'text-left'}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && editingId !== 'new' && (
              <tr>
                <td colSpan={showVanSeal ? 14 : 12} className="px-3 py-6 text-center text-gray-400 text-sm">
                  No line items yet. Click "Add Line" below to begin.
                </td>
              </tr>
            )}
            {lines.map((line) =>
              editingId === line._id ? renderEditRow(line._id) : renderReadOnlyRow(line)
            )}
            {editingId === 'new' && renderEditRow('new')}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-4">
        <button
          onClick={handleAddLine}
          disabled={editingId !== null}
          className="flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-800 disabled:opacity-40"
        >
          <PlusCircleIcon className="h-4 w-4" />
          Add Line
        </button>

        {lineStatus && (
          <span className={`text-xs px-2 py-1 rounded ${
            lineStatus.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {lineStatus.msg}
          </span>
        )}
      </div>

      {/* Footer summary */}
      <div className="border-t border-gray-200 pt-3">
        <div className="flex flex-col items-end gap-0.5 text-sm">
          <div className="flex gap-4">
            <span className="text-gray-500 w-36 text-right">Total Quantity</span>
            <span className="w-32 text-right text-gray-700">{PHP(totalQty)}</span>
          </div>
          <div className="flex gap-4">
            <span className="text-gray-500 w-36 text-right">{`Total Amount (min ₱${PHP(minAmount)})`}</span>
            <span className="w-32 text-right text-gray-700">₱{PHP(totalAmount)}</span>
          </div>
          <div className="flex gap-4">
            <span className="text-gray-500 w-36 text-right">Net of VAT</span>
            <span className="w-32 text-right text-gray-700">₱{PHP(netOfVAT)}</span>
          </div>
          <div className="flex gap-4">
            <span className="text-gray-500 w-36 text-right">VAT</span>
            <span className="w-32 text-right text-gray-700">₱{PHP(vat)}</span>
          </div>
          {hasWithholding && (
            <div className="flex gap-4">
              <span className="text-gray-500 w-36 text-right">Withholding</span>
              <span className="w-32 text-right text-gray-700">₱{PHP(withholding)}</span>
            </div>
          )}
          <div className="flex gap-4 mt-1 border-t border-gray-300 pt-1">
            <span className="font-semibold text-gray-800 w-36 text-right">Amount Due</span>
            <span className="w-32 text-right font-semibold text-base text-gray-900">₱{PHP(amountDue)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

WaybillLineTable.propTypes = {
  waybillId:      PropTypes.string.isRequired,
  hasWithholding: PropTypes.bool,
  showVanSeal:    PropTypes.bool,
  initialVanNo:   PropTypes.string,
  initialSealNo:  PropTypes.string,
  uomOptions:     PropTypes.arrayOf(PropTypes.shape({
    _id:               PropTypes.string,
    UnitOfMeasureName: PropTypes.string,
    Abbreviation:      PropTypes.string,
  })),
};

